/**
 * @file 毎日の取り組みの記録 (progress logs) の取得・追加・削除。
 *
 * @what: 学習者を選ぶと直近の記録を取得し、追加は即 POST (draft を介さない)、削除は即 DELETE。
 *   「もっと見る」で cursor の続きを読み足す。一覧は日付の新しい順に並べ直して持つ
 *   (model/progressLogOrder.ts。過去の日付で足した記録がその日付の場所に出る)。
 * @why: 記録は append 型で「その場で 1 件残す」操作なので、plan の draft + 保存バーとは
 *   ライフサイクルを分ける (未保存状態を作らない)。
 * @failure: 取得失敗は status "error"、追加/削除失敗は actionError に文言 (詳細は console)。
 */

import { useCallback, useEffect, useState } from "react";
import {
  deleteProgressLog,
  fetchProgressLogs,
  postProgressLog,
  type StudyProgressLog,
  type StudyProgressLogInput,
} from "../api/plans";
import { deleteStudyLogImage } from "../lib/studyLogImage";
import { sortProgressLogsByDate } from "../model/progressLogOrder";

export type ProgressLogsState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; items: StudyProgressLog[]; nextCursor?: string };

export function useProgressLogs(familyId: string, personId: string | null) {
  const [state, setState] = useState<ProgressLogsState>({ status: "loading" });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!personId) {
      setState({ status: "ready", items: [] });
      return;
    }
    const controller = new AbortController();
    setState({ status: "loading" });
    setActionError(null);
    fetchProgressLogs(familyId, personId, undefined, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return;
        const next: ProgressLogsState = {
          status: "ready",
          items: sortProgressLogsByDate(page.items),
        };
        if (page.next_cursor) next.nextCursor = page.next_cursor;
        setState(next);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load progress logs", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, personId]);

  const add = useCallback(
    async (input: StudyProgressLogInput): Promise<StudyProgressLog | null> => {
      if (!personId || busy) return null;
      setBusy(true);
      setActionError(null);
      try {
        const created = await postProgressLog(familyId, personId, input);
        // サーバは同じ id の再送を上書きとして扱う (再試行安全)。すでに一覧にある id なら
        // その場で差し替える。※なぜなら先頭に足すと、記録した直後のひとこと追記で同じ記録が
        // 2 行に見えるためである。
        setState((prev) => {
          if (prev.status !== "ready") return prev;
          const at = prev.items.findIndex((l) => l.id === created.id);
          const items = [...prev.items];
          if (at < 0) items.push(created);
          else items[at] = created;
          // 過去の日付で足した記録は、その日付の場所に入る
          return { ...prev, items: sortProgressLogsByDate(items) };
        });
        return created;
      } catch (err: unknown) {
        console.error("[study] failed to add progress log", err);
        setActionError("記録できませんでした。時間を置いて再度お試しください。");
        return null;
      } finally {
        setBusy(false);
      }
    },
    [busy, familyId, personId],
  );

  const remove = useCallback(
    async (logId: string): Promise<void> => {
      if (!personId || busy) return;
      setBusy(true);
      setActionError(null);
      // 添付画像の後始末対象は削除前の一覧から特定する (updater 内の副作用は
      // React の遅延実行と StrictMode 二重実行があるため使わない)
      const removed =
        state.status === "ready" ? state.items.find((l) => l.id === logId) : undefined;
      try {
        await deleteProgressLog(familyId, personId, logId);
        setState((prev) =>
          prev.status === "ready"
            ? { ...prev, items: prev.items.filter((l) => l.id !== logId) }
            : prev,
        );
        // best-effort (失敗しても記録の削除は成立させる)。暗号化の有無によらず
        // 実体を消す (enc つきを残すと参照元なしの家族データが Storage に滞留する)
        if (removed?.image_path) deleteStudyLogImage(removed.image_path);
      } catch (err: unknown) {
        console.error("[study] failed to delete progress log", err);
        setActionError("削除できませんでした。時間を置いて再度お試しください。");
      } finally {
        setBusy(false);
      }
    },
    [busy, familyId, personId, state],
  );

  const loadMore = useCallback(async (): Promise<void> => {
    if (!personId || busy || state.status !== "ready" || !state.nextCursor) return;
    setBusy(true);
    try {
      const page = await fetchProgressLogs(familyId, personId, state.nextCursor);
      setState((prev) => {
        if (prev.status !== "ready") return prev;
        const next: ProgressLogsState = {
          status: "ready",
          items: sortProgressLogsByDate([...prev.items, ...page.items]),
        };
        if (page.next_cursor) next.nextCursor = page.next_cursor;
        return next;
      });
    } catch (err: unknown) {
      console.error("[study] failed to load more progress logs", err);
      setActionError("続きを読み込めませんでした。時間を置いて再度お試しください。");
    } finally {
      setBusy(false);
    }
  }, [busy, familyId, personId, state]);

  return { state, add, remove, loadMore, busy, actionError };
}
